import { createContext, useState, useEffect, useContext } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";

import { useLDClient, useFlags } from "launchdarkly-react-client-sdk";

import { useTestingFlags } from "./LaunchDarklyProvider.tsx";
import { WidenLiterals } from "@app/types/typeHelpers.ts";

export type AuthStatus =
  | "idle"
  | "loading"
  | "authenticated"
  | "unauthenticated"
  | "error";

export type FlagsStatus = "idle" | "loading" | "loaded" | "error";

export type SubscriptionStatus =
  | "forge-enterprise"
  | "forge-internal"
  | "forge-registered"
  | "forge-subscribe";

export type UnixTimestamp = number;

export type User = {
  first_name: string;
  last_name: string;
  email: string;
  oxygen_id: string;
  analytics_id: string;
  groups: string[];
  is_internal: boolean;
  created_at: UnixTimestamp | null;
  tos_agreement: string | null;
  profile_completed_at: string | null;
  account_purpose: "BUSINESS" | "OTHER" | null;
  oxygen_avatar_url: string;
  subscription_status: SubscriptionStatus;
  is_temporary: boolean;
};

export type UnauthenticatedUserResponse = {
  message: string;
};

export type VariationFunction = <T>(
  flagName: string,
  defaultValue: T
) => WidenLiterals<T>;

export type AuthContext = {
  user: User | null;
  isInternalUser: boolean;
  authStatus: AuthStatus;
  flagsStatus: FlagsStatus;
  variation: VariationFunction;
};

const AuthContextImplementation = createContext<AuthContext>({
  user: null,
  isInternalUser: false,
  authStatus: "loading",
  flagsStatus: "idle",
  variation: <T,>(_flagName: string, _defaultValue: T) =>
    _defaultValue as WidenLiterals<T>,
});

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isInternalUser, setIsInternalUser] = useState(false);
  const [authStatus, setAuthStatus] = useState<AuthStatus>("idle");
  const [flagsStatus, setFlagsStatus] = useState<FlagsStatus>("idle");
  const [offlineFlags, setOfflineFlags] = useState<any>({});

  const location = useLocation();
  const flags = useFlags();
  const testingFlags = useTestingFlags();

  const context = {
    kind: "user",
    key: user?.analytics_id,
    groups: user?.groups,
    is_internal: user?.is_internal,
  };

  /*
    Contexts are a LaunchDarkly feature that allow you to target feature flags to
    authenticated and unauthenticated users by specific attributes.

    For future consideration, we can create multiple contexts to further target
    specific individuals.

    Example:
      const newMultiContext = {
        kind: "multi",
        user: {
          key: "user-key-123abc",
          name: "Anna",
          role: "doctor",
        },
        device: {
          key: "device-key-123abc",
          platform: "Android",
        },
      };

    This will create two different context kinds: User and Device.
    Within each context, we can then create a targeting rule based on an attribute.
    More info can be found in LaunchDarkly docs:
    https://docs.launchdarkly.com/home/flags/targeting-rules#attributes
  */

  const launchDarklyClient = useLDClient();
  useEffect(
    function setLaunchDarklyUser() {
      if (authStatus === "idle" || authStatus === "loading") return;

      // Without a signed-in user the anonymous flags are the final answer.
      if (!user?.analytics_id) {
        setFlagsStatus("loaded");
        return;
      }

      if (!launchDarklyClient) {
        setFlagsStatus("error");
        return;
      }

      let cancelled = false;
      setFlagsStatus("loading");
      launchDarklyClient
        .identify(context)
        .then(() => {
          if (!cancelled) setFlagsStatus("loaded");
        })
        .catch((error) => {
          console.error("Failed to identify LaunchDarkly user:", error);
          if (!cancelled) setFlagsStatus("error");
        });

      return function cleanup() {
        cancelled = true;
      };
    },
    [user?.analytics_id, launchDarklyClient, authStatus]
  );

  useEffect(
    function getUser() {
      let componentIsUnmounted = false;

      axios
        .get<User | UnauthenticatedUserResponse>("/user/status")
        .then(({ data }) => {
          if (componentIsUnmounted) return;

          const isAuthenticated = "oxygen_id" in data;
          if (isAuthenticated) {
            setAuthStatus("authenticated");
            setUser(data);
            setIsInternalUser(isAuthenticated && data.is_internal);

            document?.body.classList.add("is-user");
          } else {
            setAuthStatus("unauthenticated");
            setUser(null);
            document?.body.classList.remove("is-user");
          }
        })
        .catch(() => {
          if (componentIsUnmounted) return;

          document?.body.classList.remove("is-user");
          setAuthStatus("error");
          setUser(null);
        });

      return function cleanup() {
        componentIsUnmounted = true;
      };
    },
    [location.pathname]
  );

  // Loading offline flags synchronously during development can hide bugs that
  // might be experienced by users in production. Loading asynchronously
  // ensures that the development experience more closely matches production.
  // Flags will only be loaded asynchronously on the first page load.
  useEffect(function loadOfflineFlagsAsynchronously() {
    let cancel = false;

    if (process.env.NODE_ENV === "development") {
      if (cancel) return;

      // ES imports **cannot** lazily evaluate imports, so the ESLint rule that
      // prevents this behavior must be disabled.
      // eslint-disable-next-line
      const offlineFlags = require(process.env.OFFLINE_FLAGS_FILE_PATH!).default
        .flagValues;

      // Prevent unnecessary state updates & re-renders.
      if (Object.keys(offlineFlags).length === 0) {
        return;
      }
      setOfflineFlags(offlineFlags);
    }

    return function cleanup() {
      cancel = true;
    };
  }, []);

  function variation<T>(flagName: string, defaultValue: T): WidenLiterals<T> {
    // Note: do not reorder, the order is important!
    const combinedFlags = {
      ...flags,
      ...offlineFlags,
      ...testingFlags,
    };

    if (flagName in combinedFlags) return combinedFlags[flagName];

    return defaultValue as WidenLiterals<T>;
  }

  return (
    <AuthContextImplementation.Provider
      value={{ user, isInternalUser, authStatus, flagsStatus, variation }}
    >
      {children}
    </AuthContextImplementation.Provider>
  );
};

export function useAuth() {
  return useContext(AuthContextImplementation);
}
